import { Flex, Radio, Spin } from 'antd'
import { RadioChangeEvent } from 'antd/es/radio/interface'
import dayjs from 'dayjs'
import { isEmpty, maxBy, round } from 'lodash-es'
import { FunctionComponent, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { ApexCharts } from '@/components/common/apex-charts'
import Blur from '@/components/common/blur'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailDataBlockSubHeader from '@/components/kol/common/kol-detail-data-block-sub-header'
import Blocker from '@/components/kol/detail/blocker'
import { NoData } from '@/components/kol/detail/no-data'
import useKolAnalyze from '@/components/kol/hooks/use-kol-analyze'
import { mockFixedPostFrequencyData } from '@/constants/kol-detail/mock-data'
import {
  DURATION_MONTH_INTEGER_MAP,
  KolDetailDuration,
} from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { Platform } from '@/utils/convert-platform'
import {
  EngageData,
  groupPostByHourOfWeek,
} from '@/utils/detail/group-by-granularity'
import inTimeBoundDate from '@/utils/detail/in-time-bound-date'

interface HeatMapCategorySelectorProps {
  value?: keyof EngageData
  onChange: (e: RadioChangeEvent) => void
  disabled: boolean
}

const HeatMapCategorySelector: FunctionComponent<
  HeatMapCategorySelectorProps
> = ({ value, onChange, disabled }) => {
  const { formatMessage } = useIntl()
  const options = [
    { label: formatMessage({ id: 'kol:card_content' }), value: 'count' },
    {
      label: formatMessage({ id: 'kol:post_option_engagement' }),
      value: 'interactive',
    },
    { label: formatMessage({ id: 'kol:post_option_view' }), value: 'view' },
  ]

  return (
    <Radio.Group
      disabled={disabled}
      optionType='button'
      options={options}
      value={value}
      onChange={onChange}
    />
  )
}

const Steps = 5

interface PublicTimeAnalysisProps {
  kolInfo?: KolSchemaModel
  platform: Platform
  duration: KolDetailDuration
  isFetchingKolInfo: boolean
  isKolInfoLocked: boolean
  isPublicPage: boolean
  showMockData?: boolean
}

const PublicTimeAnalysis: FunctionComponent<PublicTimeAnalysisProps> = ({
  kolInfo,
  platform,
  isKolInfoLocked,
  isFetchingKolInfo,
  duration,
  isPublicPage,
  showMockData,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const theme = useTheme()

  const { analyze, isFetching: isFetchingKolAnalyze } = useKolAnalyze({
    kolId: kolInfo?.id,
    kolDcIdHashed: kolInfo?.dcIdHashed,
    isKolInfoLocked,
    platform: platform.code,
  })

  const [heatMapCategory, setHeatMapCategory] =
    useState<keyof EngageData>('interactive')

  const timeRange = DURATION_MONTH_INTEGER_MAP[duration]
  const analyzes = isKolInfoLocked ? mockFixedPostFrequencyData : analyze

  const inTimeData = inTimeBoundDate(analyzes ?? [], timeRange, 'publishedAt')

  const isFetching = isFetchingKolInfo || isFetchingKolAnalyze
  const noData = isEmpty(inTimeData) || isFetching

  const heatMapSeries = groupPostByHourOfWeek(inTimeData).map((heat) => {
    return {
      ...heat,
      name: heat.name,
      data: heat.data.map((heatData) => {
        return { ...heatData, y: heatData[heatMapCategory] }
      }),
    }
  })

  const max =
    maxBy(
      heatMapSeries.map((heat) => maxBy(heat.data, 'y')),
      'y',
    )?.y ?? 100

  const heatGaps = useMemo(
    () =>
      Array(Steps)
        .fill(max / Steps)
        .map((gap, index) => {
          return round(gap * (index + 1), 1)
        }),
    [max],
  )

  const options = {
    chart: {
      height: '400',
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      fontSize: '0px',
      markers: {
        width: 66,
        height: 15,
        radius: 0,
        strokeWidth: 0,
      },
      position: 'top',
      horizontalAlign: 'left',
      itemMargin: {
        horizontal: 0,
        vertical: 0,
      },
    },
    xaxis: {
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: false,
      labels: {
        formatter: (value: string): string => {
          return dayjs().locale('en').day(Number(value)).format('ddd')
        },
      },
    },
    plotOptions: {
      heatmap: {
        colorScale: {
          ranges: [
            {
              from: 0,
              to: heatGaps[0],
              color: theme.colors.base.blue.blue8,
            },
            {
              from: heatGaps[0],
              to: heatGaps[1],
              color: theme.colors.base.blue.blue7,
            },
            {
              from: heatGaps[1],
              to: heatGaps[2],
              color: theme.colors.base.blue.blue5,
            },
            {
              from: heatGaps[2],
              to: heatGaps[3],
              color: theme.colors.base.blue.blue3,
            },
            {
              from: heatGaps[3],
              to: heatGaps[4],
              color: theme.colors.base.blue.blue1,
            },
          ],
        },
      },
    },
    yaxis: {
      align: 'right',
      opposite: true,
      labels: {
        formatter: (value: string): string => {
          const hour = parseInt(value)
          return hour % 2 === 0
            ? dayjs().locale('en').hour(hour).format('ha')
            : ''
        },
      },
    },
    tooltip: {
      y: {
        formatter: (value: number): string => {
          return formatNumber(value)
        },
        title: {
          formatter: (
            value: string,
            opt: { dataPointIndex: number },
          ): string => {
            const time = dayjs()
              .locale('en')
              .weekday(opt.dataPointIndex)
              .format('ddd')
            const hour = dayjs().locale('en').hour(parseInt(value)).format('ha')
            return `${time} ${hour}： `
          },
        },
      },
    },
  }

  return (
    <KolDetailDataBlock
      colSpan={12}
      header={{
        title: formatMessage(
          { id: 'kol:post_time_title' },
          { platform: platform.title },
        ),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Post time analysis'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <Flex vertical gap={30} style={{ height: '100%' }}>
        <KolDetailDataBlockSubHeader
          extraContent={
            <HeatMapCategorySelector
              disabled={noData}
              value={noData ? undefined : heatMapCategory}
              onChange={(e): void => setHeatMapCategory(e.target.value)}
            />
          }
          title={formatMessage({ id: 'kol:post_heat_map_description' })}
          tooltip={{
            title: formatMessage({ id: 'kol:post_heat_map_subtitle' }),
          }}
        />
        {isFetching || isFetchingKolInfo ? (
          <Flex align='center' justify='center' style={{ flex: 1 }}>
            <Spin />
          </Flex>
        ) : (
          <Spin indicator={<Blocker />} spinning={isPublicPage}>
            <Blur activate={isKolInfoLocked && !showMockData}>
              {noData ? (
                <NoData />
              ) : (
                <>
                  <HeatmapLegend>
                    <div>
                      <span>0</span>
                    </div>
                    {heatGaps.map((gap, index) => (
                      <div key={index}>
                        <span>
                          {formatNumber(gap, { isCompactDisplay: true })}
                        </span>
                      </div>
                    ))}
                  </HeatmapLegend>
                  <ChartContainer>
                    <ApexCharts
                      options={options}
                      series={heatMapSeries}
                      type='heatmap'
                    />
                  </ChartContainer>
                </>
              )}
            </Blur>
          </Spin>
        )}
      </Flex>
    </KolDetailDataBlock>
  )
}

export default PublicTimeAnalysis

const HEATMAP_MARKER_HEIGHT = 12
const HEATMAP_MARKER_WIDTH = 66
const HeatmapLegend = styled.div`
  position: absolute;
  top: 20px;
  display: flex;
  justify-content: space-between;
  padding: 0 0 0 14px;
  gap: 1px;

  > div {
    width: ${HEATMAP_MARKER_WIDTH}px;
    position: relative;

    span {
      position: absolute;
      left: 0;
      transform: translateX(-50%);
      font-size: 12px;
      color: ${({ theme }): string => theme.colors.text.tooltip};
    }
  }
`

const ChartContainer = styled.div`
  .apexcharts-legend {
    padding: 0 14px;
  }

  .apexcharts-legend-marker {
    height: ${HEATMAP_MARKER_HEIGHT}px !important;
    width: ${HEATMAP_MARKER_WIDTH}px !important;
    overflow: hidden;

    svg {
      transform: scale(10) !important;
    }
  }
`
